import { theme } from 'aimo-ui';
import DropDownButton from 'components/common/DropDownButton';
import Icon from 'components/icons/Icon';
import useAddPaymentMethod from 'hooks/useAddPaymentMethod';
import useGetPoolingInfo from 'hooks/useGetPoolingInfo';
import useSelectedParkingVehicle from 'hooks/useSelectedParkingVehicle';
import useSelectedPaymentMethod, {
  SelectedPaymentMethod,
} from 'hooks/useSelectedPaymentMethod';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { parkingPaymentCardId } from 'states/common';
import { useParkingStore } from 'stores/parkingStore';
import styled from 'styled-components/native';
import { CenterVerticalRow, ColumnWithMargins } from 'styles/ContainerStyles';
import { CountryCode, PaymentOption } from 'types/generatedSchemaTypes';
import { isAndroid, isWeb } from 'utils/deviceUtils';
import {
  getPaymentMethodInfo,
  isCorporateAccountPaymentOption,
  isPaymentOptionValidForVehicle,
} from 'utils/paymentUtils';
import { useUiStore } from 'stores/uiStore';

const PaymentCardDropDownWrapper = styled.View`
  flex: 1 0 0;
  max-width: ${() => (isWeb || isAndroid ? '100%' : '50%')};
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const PaymentCardDropDown: React.FC<{
  showInfoIconIfNotSelected?: boolean;
  parkingZoneUid?: string;
  parkingZoneCountryCode?: CountryCode;
  isAnpr?: boolean;
  isFreeSalesOrder?: boolean;
  isFailedRecurringPaymentForSalesOrder?: boolean;
  currentPaymentMethodId?: string;
  setCardState?: (selectedPaymentMethod: SelectedPaymentMethod) => void;
  recurringOnly?: boolean;
}> = ({
  showInfoIconIfNotSelected,
  parkingZoneUid,
  parkingZoneCountryCode,
  isAnpr,
  isFreeSalesOrder,
  isFailedRecurringPaymentForSalesOrder,
  currentPaymentMethodId,
  setCardState,
  recurringOnly,
}) => {
  const { t } = useTranslation();
  const paymentMethod = useSelectedPaymentMethod(currentPaymentMethodId);
  const selectedVehicle = useSelectedParkingVehicle();
  const { companyPays, userMightHaveToPay } = useGetPoolingInfo(parkingZoneUid);
  const { addCard, loadingAddCard } = useAddPaymentMethod();
  const { setCurrentPaymentMethodId } = useParkingStore((state) => state);
  useEffect(() => {
    if (setCardState && paymentMethod) {
      setCardState(paymentMethod);
    }
  }, [setCardState, paymentMethod]);

  const getPaymentModalType = () => {
    if (isAnpr) return 'anprPaymentInfo';
    if (recurringOnly) return 'subscriptionPaymentMethod';
    return 'parkingPaymentMethod';
  };

  const handleOnPress = async (): Promise<void> => {
    if (!paymentMethod) {
      await addCard();
      return;
    }
    useUiStore.getState().setSheetModal(getPaymentModalType());
  };

  const isCreditCard = paymentMethod?.cardId ?? false;

  const { text: paymentMethodText, icon: paymentMethodIcon } =
    getPaymentMethodInfo(
      isCreditCard,
      paymentMethod,
      parkingZoneCountryCode,
      t
    );
  const getType = () => {
    if (isCreditCard) {
      return paymentMethod?.lastNums;
    }
    if (isCorporateAccountPaymentOption(paymentMethod)) {
      return paymentMethod?.optionId;
    }
    return 'billing';
  };

  const type = getType();

  const showInfoIcon =
    (showInfoIconIfNotSelected && !paymentMethod) ||
    isFailedRecurringPaymentForSalesOrder;
  const showAnprCardInfo = isAnpr && userMightHaveToPay;

  useEffect(() => {
    if (companyPays || isAnpr) {
      parkingPaymentCardId(undefined);
    }
  }, [companyPays, isAnpr]);

  const deselectPaymentOptionIfNeeded = useCallback(() => {
    // Check is only needed when a corporate account payment options is selected
    if (paymentMethod && isCorporateAccountPaymentOption(paymentMethod)) {
      // Check if the current payment option is still valid for the selected vehicle
      if (
        !isPaymentOptionValidForVehicle(
          paymentMethod as PaymentOption,
          selectedVehicle
        )
      ) {
        // Auto-deselect the invalid payment option
        parkingPaymentCardId(undefined);
        setCurrentPaymentMethodId?.(undefined);
      }
    }
  }, [paymentMethod, selectedVehicle, setCurrentPaymentMethodId]);

  // Auto-deselect payment option if it becomes invalid for the selected vehicle
  useEffect(() => {
    deselectPaymentOptionIfNeeded();
  }, [deselectPaymentOptionIfNeeded]);

  const handlePoolingPress = () => {
    if (isFreeSalesOrder) {
      return null;
    }
    if (companyPays) {
      useUiStore.getState().setSheetModal('noPaymentRequired');
    } else if (!paymentMethod) {
      addCard();
    } else {
      useUiStore.getState().setSheetModal('defaultPaymentMethod');
    }
  };

  const poolingText = () => {
    if (companyPays || isFreeSalesOrder) {
      return t('permit.access.paymentNotRequired');
    } else if (!paymentMethod) {
      return t('onboarding.addPaymentMethod');
    }
    return paymentMethodText ?? '';
  };

  const selectMethodText = () => {
    if (!paymentMethod) {
      return t('onboarding.addPaymentMethod');
    }
    return paymentMethodText ?? t('parking.parkingProcess.noCardSelected');
  };

  let leftIcon = <></>;
  if (paymentMethod) {
    leftIcon = (
      <>
        {isAnpr && (
          <Icon testID={`credit-card-icon-${type}`} name="check" size="s" />
        )}
        <Icon
          testID={`payment-method-icon-${type}`}
          name={`${paymentMethodIcon}`}
          size="l"
          color={isCreditCard ? undefined : theme.colors.boost.green}
        />
      </>
    );
  }

  if (companyPays || showAnprCardInfo || isFreeSalesOrder) {
    return (
      <PaymentCardDropDownWrapper>
        <DropDownButton
          testID="payment-card-dropdown"
          disabled={loadingAddCard}
          onPress={handlePoolingPress}
          text={poolingText()}
          leftItem={
            showAnprCardInfo &&
            paymentMethod && (
              <CenterVerticalRow>
                <ColumnWithMargins mr={5}>
                  <Icon color={theme.colors.gray300} name="check" size="m" />
                </ColumnWithMargins>
                {leftIcon}
              </CenterVerticalRow>
            )
          }
          showInfoIcon={!!paymentMethod || companyPays || isFreeSalesOrder}
          infoIconColor={theme.colors.black}
          hideDownArrow={!!paymentMethod || companyPays || isFreeSalesOrder}
        />
      </PaymentCardDropDownWrapper>
    );
  }

  return (
    <PaymentCardDropDownWrapper>
      <DropDownButton
        testID="payment-card-dropdown"
        disabled={loadingAddCard}
        onPress={handleOnPress}
        text={selectMethodText()}
        leftItem={leftIcon}
        showInfoIcon={showInfoIcon}
        justifyContent="flex-end"
        hideDownArrow={isAnpr}
        infoIconColor={
          isFailedRecurringPaymentForSalesOrder
            ? theme.colors.aimoUiRed
            : theme.colors.black
        }
      />
    </PaymentCardDropDownWrapper>
  );
};

export default PaymentCardDropDown;
